/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import ToastError from "../../components/molecules/ToastError"
import Basket from "../../components/organisms/Basket"
import CompanyBlocBpls from "../../components/organisms/BplsComponents/CompanyBlocBpls"
import { Header } from "../../components/organisms/Header"
import { Topbar } from "../../components/organisms/Topbar"
import { TENANTS } from "../../constants"
import { getCountryFromLanguage, initPdiComponent } from "../../core/service/BusinessLogicService"
import { countCompletedFields, getFieldsFiltered, loc } from "../../core/utils"
import { finalFormValuesToTunnelData, tunnelDataToFinalForm } from "../../mappers/tunnelDataMapper"
import { PackServiceContext } from "../ServicePage/context/PackServiceContext"
import { withUpdatedTunnelData } from "../hoc/withUpdatedTunnelData"
import {
  BankCoordinatesBloc,
  BudgetBloc,
  ClientPageFooter,
  CompanyBloc,
  CoordinatesBloc,
  IdentityBloc,
  IdentityDocumentsBloc,
  MyAddressesBloc,
  SignatoryBloc,
  SituationBloc,
  SubmitButton
} from "./components"
import TouchInitialInvalidFields from "./utils/touchInitialInvalidFields"
import decorator from "./decorator/decorator"
import { loadBicNlOnce } from "./validator/bicNlStore"
import validateBE from "./validator/validatorByCountry/BEValidator"
import validateES from "./validator/validatorByCountry/ESValidator"
import validateFR from "./validator/validatorByCountry/FRValidator"
import validateIT from "./validator/validatorByCountry/ITValidator"
import validateNL from "./validator/validatorByCountry/NLValidator"
import validatePT from "./validator/validatorByCountry/PTValidator"
import { setIn } from "final-form"
import arrayMutators from "final-form-arrays"
import createDecorator from "final-form-focus"
import React, { useContext, useEffect, useState } from "react"
import { Button, Col, Container, Form, Row } from "react-bootstrap"
import * as FinalForm from "react-final-form"
import { FieldArray } from "react-final-form-arrays"
import { toast } from "react-hot-toast"

const minSignatoryBloc = 1
const maxSignatoryBloc = 4
const focusOnErrors = createDecorator()

const getIgnoredFieldsForBloc = (tunnelData, bloc) => {
  const ignoredFields = []
  const blocConfiguration = tunnelData?.simplifiedJourneyInputConfiguration?.[bloc]
  if (tunnelData?.data?.order?.simplifiedJourneyEnabled && blocConfiguration) {
    ignoredFields.push(
      ...Object.entries(blocConfiguration)
        .filter(([, fieldConfiguration]) => fieldConfiguration?.editable === false)
        .map(([field]) => field)
    )
  }
  if (tunnelData?.tenant === TENANTS.SAMSUNG_ES_APZ && bloc === "identity") {
    ignoredFields.push("identityDocumentType", "identificationExpirationDate")
  }
  return [...new Set(ignoredFields)]
}

const isIgnoredRegisteredField = (fieldClientPage, ignoredFields) => {
  return ignoredFields.some((ignoredField) => fieldClientPage.endsWith("." + ignoredField))
}

function ClientPage({ tunnelData, onSetStep, history }) {
  const { packContext } = useContext(PackServiceContext)

  const [validator, setValidator] = useState(() => validateFR)

  const { tenant, shopUrl } = tunnelData

  const country = getCountryFromLanguage(tunnelData?.data?.order?.orderLanguageTag)

  useEffect(() => {
    initPdiComponent("ClientPage")
    if (country === "BE") {
      setValidator(() => validateBE)
    } else if (country === "IT") {
      setValidator(() => validateIT)
    } else if (country === "ES") {
      setValidator(() => validateES)
    } else if (country === "PT") {
      setValidator(() => validatePT)
    } else if (country === "NL") {
      setValidator(() => validateNL)
      loadBicNlOnce(tunnelData)
    }
  }, [country, tunnelData])

  const onSubmit = (values) => {
    onSetStep(finalFormValuesToTunnelData(values, country, tunnelData))
  }

  const validateFields = async (values) => {
    let errors = await validator({
      ...values,
      countryLanguage: country,
      tenant
    }).catch((invalideFields) => {
      return invalideFields.inner
        ?.filter((field) => field.errors.length > 0)
        .reduce((errors, errorField) => setIn(errors, errorField.path, errorField.message), {})
    })
    return filterErrorsToMatchClientPageConfig(errors)
  }
  const filterErrorsToMatchClientPageConfig = (errors) => {
    if (!errors || typeof errors !== "object") {
      return {}
    }
    let filtredErrors = {}
    tunnelData.clientPageConfiguration.identity = tunnelData.clientPageConfiguration.identity
      ? tunnelData.clientPageConfiguration.identity
      : tunnelData.clientPageConfiguration.leaderIdentity
    tunnelData.clientPageConfiguration.coordinates = tunnelData.clientPageConfiguration.coordinates
      ? tunnelData.clientPageConfiguration.coordinates
      : tunnelData.clientPageConfiguration.leaderCoordinates
    try {
      for (let [bloc, fields] of Object.entries(errors)) {
        let filtredFields = {}
        let signatoryBlocErrorsSavedByBlocIndex = []
        if (bloc === "signatories" && tunnelData.clientPageConfiguration["signatory"]) {
          for (let [blocIndex, blocErrors] of Object.entries(fields)) {
            if (blocErrors !== null && typeof blocErrors === "object") {
              const signatories = Object.fromEntries(Object.entries(blocErrors))
              signatoryBlocErrorsSavedByBlocIndex[blocIndex] = signatories
              filtredErrors = {
                ...filtredErrors,
                signatories: signatoryBlocErrorsSavedByBlocIndex
              }
            }
          }
        } else if (tunnelData.clientPageConfiguration[bloc]) {
          const ignoredFields = getIgnoredFieldsForBloc(tunnelData, bloc)
          for (let [field, errorMessage] of Object.entries(fields)) {
            if (tunnelData.clientPageConfiguration[bloc].includes(field) && !ignoredFields.includes(field)) {
              filtredFields[field] = errorMessage
            }
          }
          filtredErrors[bloc] = filtredFields
        }
      }
      return filtredErrors
    } catch (error) {
      console.error(error)
      toast.error(
        (t) => (
          <ToastError message={"Validateur HS : " + error?.message} handlePictoClick={() => toast.dismiss(t.id)} />
        ),
        {
          id: error,
          duration: Infinity
        }
      )
      return {}
    }
  }

  let customer = tunnelData?.data?.order?.customer
  let person = tunnelData?.data?.order?.customer?.person

  if (tunnelData?.comeBackToClientStep) {
    customer = tunnelData?.data?.customer
    person = tunnelData?.data?.person
  }
  if (tenant === TENANTS.BPLS) {
    person = tunnelData?.data?.order?.customer
  }

  const handleDecorator = React.useMemo(
    () => decorator(tunnelData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const UpdatedTunnelDataBasket = withUpdatedTunnelData(Basket, tunnelData, packContext)

  return (
    <Container fluid className="white-background">
      <Row noGutters>
        <Col className="main-block" lg={8}>
          <Row>
            <Topbar tunnelData={tunnelData} history={history} />
            <Header tunnelData={tunnelData} />
            <Col sm={12}>
              <FinalForm.Form
                initialValues={tunnelDataToFinalForm(person, customer, country, tunnelData)}
                keepDirtyOnReinitialize
                decorators={[handleDecorator, focusOnErrors]}
                mutators={{
                  ...arrayMutators
                }}
                validate={validateFields}
                onSubmit={onSubmit}
                render={({
                  handleSubmit,
                  values,
                  errors,
                  hasValidationErrors,
                  form: {
                    mutators: { push },
                    ...formProps
                  }
                }) => {
                  const identityIgnoredFields = getIgnoredFieldsForBloc(tunnelData, "identity")
                  const identityBlocFields = getFieldsFiltered(formProps.getRegisteredFields(), "identity.")
                  const identityCountedBlocFields = identityBlocFields.filter(
                    (fieldClientPage) => !isIgnoredRegisteredField(fieldClientPage, identityIgnoredFields)
                  )
                  const identityCounterBlocFields =
                    identityCountedBlocFields.length > 0 ? identityCountedBlocFields : identityBlocFields
                  const identityCounterIgnoredFields = identityCountedBlocFields.length > 0 ? identityIgnoredFields : []
                  return (
                    <Form onSubmit={handleSubmit}>
                      <TouchInitialInvalidFields />
                      <Container>
                        <div className="d-flex justify-content-center margin-bottom-40">
                          {tunnelData.data.order.simplifiedJourneyEnabled &&
                            tunnelData.simplifiedJourneySupportEmail &&
                            loc("change_identity_bloc_simplified_journey").replace(
                              "{adresse}",
                              tunnelData.simplifiedJourneySupportEmail
                            )}
                        </div>
                        <Row>
                          {tenant === TENANTS.BPLS ? (
                            <CompanyBlocBpls
                              tunnelData={tunnelData}
                              values={values}
                              errors={errors.company}
                              country={country}
                              blocFields={getFieldsFiltered(formProps.getRegisteredFields(), "company.")}
                              completedFields={countCompletedFields(
                                getFieldsFiltered(formProps.getRegisteredFields(), "company."),
                                hasValidationErrors,
                                errors?.company
                              )}
                            />
                          ) : (
                            <CompanyBloc
                              tunnelData={tunnelData}
                              values={values}
                              errors={errors.company}
                              country={country}
                              blocFields={getFieldsFiltered(formProps.getRegisteredFields(), "company.")}
                              completedFields={countCompletedFields(
                                getFieldsFiltered(formProps.getRegisteredFields(), "company."),
                                hasValidationErrors,
                                errors?.company
                              )}
                            />
                          )}
                          <BankCoordinatesBloc
                            tunnelData={tunnelData}
                            values={values}
                            errors={errors.bankCoordinates}
                            country={country}
                            blocFields={getFieldsFiltered(formProps.getRegisteredFields(), "bankCoordinates.")}
                            completedFields={countCompletedFields(
                              getFieldsFiltered(formProps.getRegisteredFields(), "bankCoordinates."),
                              hasValidationErrors,
                              errors?.bankCoordinates
                            )}
                          />
                          <MyAddressesBloc
                            tunnelData={tunnelData}
                            values={values}
                            errors={errors.myAddresses}
                            country={country}
                            blocFields={getFieldsFiltered(formProps.getRegisteredFields(), "myAddresses.")}
                            completedFields={countCompletedFields(
                              getFieldsFiltered(formProps.getRegisteredFields(), "myAddresses."),
                              hasValidationErrors,
                              errors?.myAddresses
                            )}
                          />

                          <FieldArray name="signatories">
                            {({ fields }) => {
                              return (
                                <>
                                  {fields.map((name, index) => {
                                    return (
                                      <SignatoryBloc
                                        key={name + index}
                                        tunnelData={tunnelData}
                                        values={values}
                                        errors={errors.signatories?.[index]}
                                        country={country}
                                        name={name}
                                        index={index}
                                        blocFields={getFieldsFiltered(formProps.getRegisteredFields(), name)}
                                        completedFields={countCompletedFields(
                                          getFieldsFiltered(formProps.getRegisteredFields(), `signatories[${index}]`),
                                          hasValidationErrors,
                                          errors?.signatories?.[index]
                                        )}
                                      >
                                        {fields.length > minSignatoryBloc && (
                                          <Button
                                            variant="outline-primary"
                                            onClick={() => fields.remove(index)}
                                            className="ico-minus-circle-before mt-3 mb-3"
                                          >
                                            {loc("Remove signatory")}
                                          </Button>
                                        )}
                                      </SignatoryBloc>
                                    )
                                  })}
                                </>
                              )
                            }}
                          </FieldArray>

                          <IdentityBloc
                            tunnelData={tunnelData}
                            values={values}
                            errors={errors.identity}
                            country={country}
                            blocFields={identityCounterBlocFields}
                            completedFields={countCompletedFields(
                              identityBlocFields,
                              hasValidationErrors,
                              errors?.identity,
                              identityCounterIgnoredFields
                            )}
                          />
                          <CoordinatesBloc
                            tunnelData={tunnelData}
                            values={values}
                            errors={errors.coordinates}
                            country={country}
                            blocFields={getFieldsFiltered(formProps.getRegisteredFields(), "coordinates")}
                            completedFields={countCompletedFields(
                              getFieldsFiltered(formProps.getRegisteredFields(), "coordinates"),
                              hasValidationErrors,
                              errors?.coordinates,
                              ["additionalAddress"]
                            )}
                          />
                          <IdentityDocumentsBloc
                            tunnelData={tunnelData}
                            values={values}
                            errors={errors.identityDocuments}
                            country={country}
                            blocFields={getFieldsFiltered(formProps.getRegisteredFields(), "identityDocuments")}
                            completedFields={countCompletedFields(
                              getFieldsFiltered(formProps.getRegisteredFields(), "identityDocuments"),
                              hasValidationErrors,
                              errors?.identityDocuments
                            )}
                          />
                          <SituationBloc
                            tunnelData={tunnelData}
                            values={values}
                            errors={errors.situation}
                            country={country}
                            blocFields={getFieldsFiltered(formProps.getRegisteredFields(), "situation")}
                            completedFields={countCompletedFields(
                              getFieldsFiltered(formProps.getRegisteredFields(), "situation"),
                              hasValidationErrors,
                              errors?.situation
                            )}
                          />
                          <BudgetBloc
                            tunnelData={tunnelData}
                            values={values}
                            errors={errors.budget}
                            country={country}
                            blocFields={getFieldsFiltered(formProps.getRegisteredFields(), "budget.")}
                            completedFields={countCompletedFields(
                              getFieldsFiltered(formProps.getRegisteredFields(), "budget."),
                              hasValidationErrors,
                              errors?.budget
                            )}
                          />
                        </Row>

                        {tenant !== TENANTS.BPLS && <SubmitButton disabled={hasValidationErrors} />}
                        {tenant === TENANTS.BPLS && (
                          <Row className="footer-fixed">
                            <Col
                              lg={{ span: 3, order: 1 }}
                              md={{ span: 4, order: 1 }}
                              xs={{ span: 6, order: 2 }}
                              className="align-item-start"
                            >
                              <Button
                                href={shopUrl}
                                variant="outline-primary"
                                className={`h-100 ico-back-small-before w-100`}
                              >
                                {loc("modifier_donnees")}
                              </Button>
                            </Col>
                            <Col
                              lg={{ span: 4, order: 2 }}
                              md={{ span: 4, order: 2 }}
                              xs={{ span: 12, order: 1 }}
                              className={`h-100 align-item-center mr-auto ml-auto mb-2 mb-md-0`}
                            >
                              <Button
                                variant={`${
                                  values?.signatories?.length >= maxSignatoryBloc ? "success" : "outline-primary"
                                }`}
                                className="ico-plus-circle-before"
                                onClick={() =>
                                  push("signatories", {
                                    title: undefined,
                                    name: undefined,
                                    firstName: undefined,
                                    email: undefined,
                                    phoneNumber: undefined
                                  })
                                }
                                disabled={values?.signatories?.length >= maxSignatoryBloc}
                              >
                                {loc("Add signatory")}
                              </Button>
                            </Col>
                            <Col
                              lg={{ span: 3, order: 3 }}
                              md={{ span: 4, order: 3 }}
                              xs={{ span: 6, order: 3 }}
                              className="align-item-end"
                            >
                              <Button
                                id="continue"
                                variant="success"
                                type="button"
                                className="h-100 btn-solid btn"
                                disabled={hasValidationErrors}
                                onClick={handleSubmit}
                              >
                                {loc`Submit_BPLS`}
                              </Button>
                            </Col>
                          </Row>
                        )}
                      </Container>
                    </Form>
                  )
                }}
              />
            </Col>
            <ClientPageFooter tunnelData={tunnelData} />
          </Row>
        </Col>
        <Col className="aside-block d-none d-lg-block d-xl-block" lg={4}>
          <UpdatedTunnelDataBasket />
        </Col>
      </Row>
    </Container>
  )
}

export default ClientPage
